// Página de autenticación: invitado, entrar, registro y recuperar contraseña
import { useState, useEffect, FormEvent } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { GoogleLogin } from '@react-oauth/google';
import { EnvelopeOpenIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { LogoMark } from '../components/Logo';
import Segmented from '../components/Segmented';
import { API_URL } from '../lib/api';


type Tab = 'login' | 'register' | 'guest' | 'forgot';

function Field({ id, label, hint, error, ...props }: React.InputHTMLAttributes<HTMLInputElement> & { id: string; label: string; hint?: string; error?: string }) {
  return (
    <div>
      <label htmlFor={id} className="label">{label}</label>
      <input id={id} className={`input ${error ? 'border-danger focus:border-danger' : ''}`} aria-invalid={!!error} {...props} />
      {error ? <p className="mt-1.5 text-caption text-danger">{error}</p> : hint && <p className="mt-1.5 text-caption text-ink-2">{hint}</p>}
    </div>
  );
}

function Divider() {
  return (
    <div className="flex items-center gap-3 text-caption text-ink-3" aria-hidden="true">
      <span className="flex-1 h-px bg-line" /> o <span className="flex-1 h-px bg-line" />
    </div>
  );
}

const prefersDark = () => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

export default function Auth() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<Tab>(
    (searchParams.get('tab') as Tab) || 'guest'
  );

  const [loginEmail, setLoginEmail]         = useState('');
  const [loginPassword, setLoginPassword]   = useState('');
  const [regUsername, setRegUsername]       = useState('');
  const [regEmail, setRegEmail]             = useState('');
  const [regPassword, setRegPassword]       = useState('');
  const [regPasswordConfirm, setRegPasswordConfirm] = useState('');
  const [guestName, setGuestName]           = useState('');
  const [forgotEmail, setForgotEmail]       = useState('');
  const [forgotSent, setForgotSent]         = useState(false);
  const [forgotLoading, setForgotLoading]   = useState(false);

  const { login, register, loginAsGuest, loginWithGoogle, isLoading, error, clearError } = useAuth();

  useEffect(() => { clearError(); }, [activeTab]);


  const handleLogin    = async (e: FormEvent) => { e.preventDefault(); await login(loginEmail, loginPassword); };
  const handleRegister = async (e: FormEvent) => { e.preventDefault(); if (regPassword !== regPasswordConfirm) return; await register(regUsername, regEmail, regPassword); };
  const handleGuest    = async (e: FormEvent) => { e.preventDefault(); await loginAsGuest(guestName); };
  const handleForgot   = async (e: FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: forgotEmail });
    } finally {
      setForgotLoading(false);
      setForgotSent(true);
    }
  };

  const tabs: { value: Exclude<Tab, 'forgot'>; label: string }[] = [
    { value: 'guest', label: 'Invitado' },
    { value: 'login', label: 'Entrar' },
    { value: 'register', label: 'Crear cuenta' },
  ];

  const mismatch = !!(regPasswordConfirm && regPassword !== regPasswordConfirm);
  const formMotion = {
    initial: { opacity: 0, y: 6 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -4 },
    transition: { type: 'spring' as const, bounce: 0, duration: 0.3 },
  };
  const googleTheme = prefersDark() ? 'filled_black' : 'outline';

  return (
    <div className="min-h-[calc(100vh-3.5rem)] grid place-items-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', bounce: 0, duration: 0.5 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <span className="inline-block"><LogoMark size={64} /></span>
          <h1 className="mt-4 text-title1 font-bold text-ink">
            {activeTab === 'register' ? 'Crea tu cuenta' : activeTab === 'forgot' ? 'Recupera el acceso' : 'Te damos la bienvenida'}
          </h1>
          <p className="mt-1 text-footnote text-ink-2">
            {activeTab === 'register' ? 'Guarda tu progreso y entra en los rankings.' : activeTab === 'forgot' ? 'Te enviaremos un enlace por correo.' : 'Entra para empezar a jugar.'}
          </p>
        </div>

        <div className="panel shadow-card p-6">
          {activeTab !== 'forgot' && (
            <div className="mb-5">
              <Segmented label="Forma de entrar" value={activeTab} options={tabs} onChange={setActiveTab} />
            </div>
          )}

          <AnimatePresence>
            {error && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-4 text-footnote text-danger"
                role="alert"
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait" initial={false}>
            {activeTab === 'guest' && (
              <motion.form key="guest" {...formMotion} onSubmit={handleGuest} className="flex flex-col gap-4">
                <p className="text-footnote text-ink-2">Entra con un nombre temporal. No guardamos datos personales.</p>
                <Field id="guest-name" label="Tu nombre" type="text" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Por ejemplo, Lucía" maxLength={15} required autoComplete="nickname" hint="Añadiremos unos números para que sea único." />
                <button type="submit" disabled={isLoading} className="btn-primary w-full">
                  {isLoading ? 'Entrando…' : 'Continuar como invitado'}
                </button>
              </motion.form>
            )}

            {activeTab === 'login' && (
              <motion.form key="login" {...formMotion} onSubmit={handleLogin} className="flex flex-col gap-4">
                <Field id="login-email" label="Correo electrónico" type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="tu@correo.com" required autoComplete="email" />
                <Field id="login-password" label="Contraseña" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required autoComplete="current-password" />
                <button type="submit" disabled={isLoading} className="btn-primary w-full">
                  {isLoading ? 'Entrando…' : 'Iniciar sesión'}
                </button>
                <button type="button" onClick={() => setActiveTab('forgot')} className="btn-plain h-8 self-center text-footnote">
                  He olvidado la contraseña
                </button>
                <Divider />
                <div className="flex justify-center">
                  <GoogleLogin onSuccess={(res) => { if (res.credential) loginWithGoogle(res.credential); }} onError={() => {}} theme={googleTheme} text="signin_with" shape="pill" />
                </div>
              </motion.form>
            )}

            {activeTab === 'register' && (
              <motion.form key="register" {...formMotion} onSubmit={handleRegister} className="flex flex-col gap-4">
                <Field id="reg-username" label="Nombre de usuario" type="text" value={regUsername} onChange={(e) => setRegUsername(e.target.value)} minLength={3} maxLength={20} required autoComplete="username" hint="Es el nombre que aparecerá en el ranking." />
                <Field id="reg-email" label="Correo electrónico" type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} placeholder="tu@correo.com" required autoComplete="email" />
                <Field id="reg-password" label="Contraseña" type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} minLength={8} required autoComplete="new-password" hint="Al menos 8 caracteres." />
                <Field id="reg-confirm" label="Repite la contraseña" type="password" value={regPasswordConfirm} onChange={(e) => setRegPasswordConfirm(e.target.value)} required autoComplete="new-password" error={mismatch ? 'Las contraseñas no coinciden.' : undefined} />
                <button type="submit" disabled={isLoading || mismatch} className="btn-primary w-full">
                  {isLoading ? 'Creando cuenta…' : 'Crear cuenta'}
                </button>
                <Divider />
                <div className="flex justify-center">
                  <GoogleLogin onSuccess={(res) => { if (res.credential) loginWithGoogle(res.credential); }} onError={() => {}} theme={googleTheme} text="signup_with" shape="pill" />
                </div>
              </motion.form>
            )}

            {activeTab === 'forgot' && (
              <motion.div key="forgot" {...formMotion} className="flex flex-col gap-4">
                {forgotSent ? (
                  <div className="text-center py-2">
                    <span className="mx-auto w-12 h-12 rounded-full bg-accent/10 text-accent grid place-items-center">
                      <EnvelopeOpenIcon className="w-6 h-6" />
                    </span>
                    <p className="mt-3 text-body font-semibold text-ink">Revisa tu correo</p>
                    <p className="mt-1 text-footnote text-ink-2">Si la dirección está registrada, te llegará un enlace para cambiar la contraseña. Caduca en una hora.</p>
                    <button type="button" onClick={() => { setActiveTab('login'); setForgotSent(false); setForgotEmail(''); }} className="btn-plain mt-4 text-footnote">
                      Volver a iniciar sesión
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgot} className="flex flex-col gap-4">
                    <Field id="forgot-email" label="Correo electrónico" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="tu@correo.com" required autoComplete="email" />
                    <button type="submit" disabled={forgotLoading} className="btn-primary w-full">
                      {forgotLoading ? 'Enviando…' : 'Enviar enlace'}
                    </button>
                    <button type="button" onClick={() => setActiveTab('login')} className="btn-plain h-8 self-center text-footnote">Volver</button>
                  </form>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p className="mt-5 text-center text-caption text-ink-2">
          Al continuar aceptas los <Link to="/terminos" className="text-accent hover:underline">términos de uso</Link>.
        </p>
      </motion.div>
    </div>
  );
}
